import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { ScheduleEvent } from '../../containers/Schedule/Schedule';

interface Props {
    events: ScheduleEvent[];
    date: Date;
    onSelectEvent: (event: ScheduleEvent) => void;
    onNavigate: (date: Date) => void;
}

const TIME_MARKS = [
    { pct: 0, label: '0:00' },
    { pct: 25, label: '6:00' },
    { pct: 50, label: '12:00' },
    { pct: 75, label: '18:00' },
    { pct: 100, label: '24:00' },
];

const timeToPercent = (h: number, m: number): number =>
    ((h * 60 + m) / 1440) * 100;

/** Returns true if [eStart, eEnd] overlaps with [dayStart, dayEnd] */
const overlapsDay = (event: ScheduleEvent, dayStart: moment.Moment, dayEnd: moment.Moment): boolean => {
    const eStart = moment(event.start);
    const eEnd = event.end ? moment(event.end) : eStart.clone().endOf('day');
    return eStart.isSameOrBefore(dayEnd) && eEnd.isSameOrAfter(dayStart);
};

/** Position/width of the bar, clamped to the day's 0–24h range */
const getBarProps = (event: ScheduleEvent, dayStart: moment.Moment, dayEnd: moment.Moment): { left: string; width: string } => {
    const eStart = moment(event.start);
    const eEnd = event.end ? moment(event.end) : dayEnd.clone();

    // Clamp to day boundaries
    const clampedStart = eStart.isBefore(dayStart) ? dayStart.clone() : eStart;
    const clampedEnd = eEnd.isAfter(dayEnd) ? dayEnd.clone() : eEnd;

    const sh = clampedStart.hours();
    const sm = clampedStart.minutes();
    const eh = clampedEnd.hours();
    const em = clampedEnd.minutes();

    const leftPct = timeToPercent(sh, sm);
    const endPct = (eh === 23 && em >= 59) ? 100 : timeToPercent(eh, em);
    const widthPct = Math.max(endPct - leftPct, 1.5);

    return { left: `${leftPct}%`, width: `${widthPct}%` };
};

/** Human-readable time range for a bar, clamped to the current day */
const getTimeLabel = (event: ScheduleEvent, dayStart: moment.Moment, dayEnd: moment.Moment): string => {
    if (event.is_time_slot && event.start_time && event.end_time) {
        return `${event.start_time.slice(0, 5)} – ${event.end_time.slice(0, 5)}`;
    }

    const eStart = moment(event.start);
    const eEnd = event.end ? moment(event.end) : null;

    const s = eStart.isBefore(dayStart) ? '00:00' : eStart.format('HH:mm');
    const e = eEnd
        ? (eEnd.isAfter(dayEnd) ? '24:00' : eEnd.format('HH:mm'))
        : '24:00';

    return `${s} – ${e}`;
};

const ScheduleTimelineView: React.FC<Props> = ({ events, date, onSelectEvent, onNavigate }) => {
    const [t, i18n] = useTranslation();

    // Ensure moment uses the same locale as the rest of the app
    const lang = (i18n.language ?? 'en').split('-')[0];
    moment.locale(lang);

    const weekStart = useMemo(() => moment(date).startOf('week'), [date, lang]);
    const weekEnd = useMemo(() => moment(weekStart).endOf('week'), [weekStart]);
    const today = moment().startOf('day');

    const days = useMemo(
        () => Array.from({ length: 7 }, (_, i) => moment(weekStart).add(i, 'days')),
        [weekStart],
    );

    // Not memoized so it always reflects the current locale on every render
    const s = weekStart.clone();
    const e = weekEnd.clone();
    const dateLabel = s.month() === e.month()
        ? `${s.format('D')} – ${e.format('D')} ${s.format('MMMM YYYY')}`
        : `${s.format('D MMM')} – ${e.format('D MMM YYYY')}`;

    // Group events by day using range overlap (not just start-date match),
    // so multi-day spanning events appear on every day they're active.
    const eventsByDay = useMemo(() => {
        const map = new Map<string, ScheduleEvent[]>();
        days.forEach(day => {
            const key = day.format('YYYY-MM-DD');
            const dayStart = day.clone().startOf('day');
            const dayEnd = day.clone().endOf('day');
            map.set(key, events.filter(e => overlapsDay(e, dayStart, dayEnd)));
        });
        return map;
    }, [events, days]);

    return (
        <div className="schedule-timeline">
            {/* Navigation bar */}
            <div className="schedule-timeline__nav">
                <div className="schedule-timeline__nav-btns">
                    <button
                        className="rbc-toolbar-btn"
                        type="button"
                        title={t('Previous')}
                        onClick={() => onNavigate(moment(date).subtract(1, 'week').toDate())}
                    >
                        <i className="iconsminds-arrow-back-3" />
                    </button>
                    <button
                        className="rbc-toolbar-btn rbc-toolbar-today-btn"
                        type="button"
                        onClick={() => onNavigate(new Date())}
                    >
                        {t('Today')}
                    </button>
                    <button
                        className="rbc-toolbar-btn"
                        type="button"
                        title={t('Next')}
                        onClick={() => onNavigate(moment(date).add(1, 'week').toDate())}
                    >
                        <i className="iconsminds-arrow-forward-2" />
                    </button>
                </div>
                <span className="schedule-timeline__date-label">{dateLabel}</span>
            </div>

            {/* Time axis header */}
            <div className="schedule-timeline__axis-row">
                <div className="schedule-timeline__label-col schedule-timeline__label-col--header" />
                <div className="schedule-timeline__axis">
                    {TIME_MARKS.map(({ pct, label }, i) => (
                        <span
                            key={pct}
                            className={`schedule-timeline__axis-mark${i === 0 ? ' schedule-timeline__axis-mark--first' : ''}${i === TIME_MARKS.length - 1 ? ' schedule-timeline__axis-mark--last' : ''}`}
                            style={{ left: `${pct}%` }}
                        >
                            {label}
                        </span>
                    ))}
                </div>
            </div>

            {/* Day rows */}
            {days.map(day => {
                const key = day.format('YYYY-MM-DD');
                const dayStart = day.clone().startOf('day');
                const dayEnd = day.clone().endOf('day');
                const dayEvents = [...(eventsByDay.get(key) ?? [])].sort((a, b) => a.priority - b.priority);
                const isToday = day.isSame(today, 'day');

                return (
                    <div
                        key={key}
                        className={`schedule-timeline__row${isToday ? ' schedule-timeline__row--today' : ''}`}
                    >
                        <div className="schedule-timeline__label-col">
                            <span className="schedule-timeline__day-name">
                                {day.format('ddd').toUpperCase()}
                            </span>
                            <span className={`schedule-timeline__day-date${isToday ? ' schedule-timeline__day-date--today' : ''}`}>
                                {day.format('D')}
                            </span>
                        </div>

                        <div className="schedule-timeline__day-grid">
                            {dayEvents.length === 0 ? (
                                <div className="schedule-timeline__empty-row" />
                            ) : (
                                <div className="schedule-timeline__bars">
                                    {dayEvents.map((event, idx) => {
                                        const { left, width } = getBarProps(event, dayStart, dayEnd);
                                        const timeLabel = getTimeLabel(event, dayStart, dayEnd);
                                        return (
                                            <div
                                                key={`${event.id}-${idx}`}
                                                className="schedule-timeline__bar-row"
                                            >
                                                <button
                                                    type="button"
                                                    className={`schedule-timeline__bar schedule-timeline__bar--p${Math.min(event.priority, 3)}`}
                                                    style={{ left, width }}
                                                    onClick={() => onSelectEvent(event)}
                                                    title={`${event.title} · ${timeLabel}`}
                                                >
                                                    <span className="schedule-timeline__bar-name">
                                                        {event.title}
                                                    </span>
                                                    <span className="schedule-timeline__bar-time">
                                                        {timeLabel}
                                                    </span>
                                                    <div className="schedule-timeline__bar-priority">
                                                        {Array.from({ length: 3 }, (_, i) => (
                                                            <span
                                                                key={i}
                                                                className={`schedule-timeline__bar-priority-seg${i <= Math.min(event.priority, 2) ? ' schedule-timeline__bar-priority-seg--filled' : ''}`}
                                                            />
                                                        ))}
                                                    </div>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default React.memo(ScheduleTimelineView);
